<template>
    <div>
        <b-modal
            ref="dataBaseModal"
            centered
            @hidden="closeDataBaseModal"
            size="lg"
            hide-footer
            :title="`${title} 데이터`"
        >
            <template #default>
                <DataTable
                    :value="tableDatas"
                    scrollable
                    :scrollHeight="'calc(100vh - 300px)'"
                    class="EquipMgmtDataModal"
                >
                    <template #header>
                        <div style="display: flex; align-items: center; justify-content: space-between;">
                            <div>
                                <span class="m-0 table-title">데이터</span>
                            </div>
                            <div>
                                <button type="button" class="btn btn-sm btn-outline-secondary" @click="exportToExcel">
                                    Excel Export
                                </button>
                            </div>
                        </div>
                    </template>
                    <Column
                        field="regDt"
                        header="날짜"
                        :headerStyle="{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            fontWeight: 'bold',
                            textAlign: 'center',
                        }"
                        :bodyStyle="{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            fontWeight: 'bold',
                            textAlign: 'center',
                        }"
                    >
                        <template #body="slotProps">
                            {{ formatRegDt(slotProps.data.regDt) }}
                        </template>
                    </Column>

                    <Column
                        v-for="(header, key) in tableHeaders"
                        :key="key"
                        :field="header.text"
                        :header="`${header.text} (${header.sumUnit})`"
                        :headerStyle="{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            textAlign: 'center',
                        }"
                        :bodyStyle="{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            textAlign: 'center',
                        }"
                    />
                </DataTable>
            </template>
        </b-modal>
    </div>
</template>

<script>
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import * as popupMessages from "@src/consts/popupMessageConsts";
import { mapState } from "vuex";
import excelHelper from "@src/libs/excelHelper.js";

export default {
    components: {
        DataTable,
        Column,
    },
    props: ["flag", "data", "title"],
    data() {
        return {
            tableDatas: [],
            tableHeaders: [],
            popupMessages,
        };
    },
    watch: {
        flag(newValue) {
            if (newValue) {
                this.showDataModal();
            } else {
                this.closeDataBaseModal();
            }
        },
        data(newValue) {
            this.tableHeaders = this.extractDataRecursive(newValue.trendData.headers);
            this.tableDatas = this.makeTableDatas(newValue);
        },
    },
    mounted() {
        if (this.data) {
            this.tableHeaders = this.extractDataRecursive(this.data.trendData.headers);
            this.tableDatas = this.makeTableDatas(this.data);
        }
    },

    computed: {
        ...mapState({
            units: (state) => state.unitCodes,
        }),
        regDtLabel() {
            return this.data[0].data.labels;
        },
    },
    methods: {
        findUnit(unitSmallCode) {
            for (const key of Object.keys(this.units)) {
                const foundUnit = this.units[key].find((item) => item.value === unitSmallCode);
                if (foundUnit) {
                    return foundUnit.text;
                }
            }
            return null;
        },
        formatRegDt(regDt) {
            if (!regDt) return "";
            return regDt.includes("T") ? regDt.split("T").join(" ") : regDt;
        },

        async exportToExcel() {
            try {
                // 헤더 정의
                const headers = [
                    "날짜",
                    ...Object.values(this.tableHeaders).map(
                        (header) => `${header.text} (${this.findUnit(header.sumUnit)})`
                    ),
                ];

                // 데이터 변환 (excelHelper 형식에 맞게)
                const excelData = this.tableDatas.map((row) => {
                    const rowData = {
                        날짜: this.formatRegDt(row.regDt),
                    };

                    // 각 헤더에 대해 데이터 추가
                    Object.values(this.tableHeaders).forEach((header) => {
                        const headerKey = `${header.text} (${this.findUnit(header.sumUnit)})`;
                        rowData[headerKey] =
                            row[header.text] !== null && row[header.text] !== undefined ? row[header.text] : "-";
                    });

                    return rowData;
                });

                // 파일명 생성 (현재 날짜 포함)
                const currentDate = new Date().toISOString().slice(0, 10);
                const fileName = `${this.title}_데이터_${currentDate}.xlsx`;

                // 컬럼 너비 설정
                const columnWidths = {
                    날짜: 20,
                };
                Object.values(this.tableHeaders).forEach((header) => {
                    const headerKey = `${header.text} (${this.findUnit(header.sumUnit)})`;
                    columnWidths[headerKey] = 15;
                });

                // 셀 타입 설정
                const cellTypes = {
                    날짜: "string",
                };
                Object.values(this.tableHeaders).forEach((header) => {
                    const headerKey = `${header.text} (${this.findUnit(header.sumUnit)})`;
                    cellTypes[headerKey] = "number";
                });

                // excelHelper를 사용하여 워크북 생성 및 다운로드
                await excelHelper.createAndDownloadWorkbook(excelData, headers, fileName, {
                    sheetName: `${this.title}_데이터`,
                    creator: "THEKIE",
                    lastModifiedBy: "THEKIE",
                    columnWidths,
                    cellTypes,
                });

                this.alertNoti(popupMessages.COMMON_EXCEL_DOWNLOAD_START_POPUP_MESSAGE);
            } catch (error) {
                console.error("엑셀 다운로드 중 오류 발생:", error);
                this.alertNoti("엑셀 다운로드 중 오류가 발생했습니다.");
            }
        },

        extractDataRecursive(node) {
            const result = {
                [node.sysNodeIdx]: {
                    text: node.text,
                    sumUnit: node.sumUnit,
                },
            };

            if (node.children && node.children.length > 0) {
                for (const child of node.children) {
                    result[child.sysNodeIdx] = {
                        text: child.text,
                        sumUnit: child.sumUnit,
                    };
                }
            }

            return result;
        },
        makeTableDatas(data) {
            const tabledata = data.trendData.results.map((result) => {
                const newResult = {};

                Object.keys(this.tableHeaders).forEach((key) => {
                    newResult[this.tableHeaders[key].text] = result[key];
                });

                newResult["regDt"] = result.regDt;

                return newResult;
            });

            return tabledata;
        },
        showDataModal() {
            if (this.$refs.dataBaseModal) {
                this.$refs.dataBaseModal.hide();
                this.$refs.dataBaseModal.show();
            }
        },
        closeDataBaseModal() {
            this.$refs.dataBaseModal.hide();
            this.$emit("closeDataBaseModal");
        },
    },
};
</script>

<style scoped></style>
